import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  Dimensions,
  Pressable,
  ScrollView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { RootStackParamList } from '../../types/navigation';
import { useTheme } from '../../hooks/useTheme';
import { useAuth } from '../../hooks/useAuth';
import { trackButtonClick } from '../../utils/analytics';
import {
  SIDEBAR_WIDTH_COLLAPSED,
  SIDEBAR_WIDTH_EXPANDED,
} from '../../constants/layout';

interface SidebarProps {
  activeTab?: string;
  onTabPress?: (tab: string) => void;
  onCollapseChange?: (collapsed: boolean) => void;
  isCollapsed?: boolean;
  onSettingsCategoryChange?: (category: string) => void;
  activeSettingsCategory?: string;
}

const Sidebar: React.FC<SidebarProps> = ({
  activeTab = 'HomeTab',
  onTabPress,
  onCollapseChange,
  isCollapsed: externalIsCollapsed,
  onSettingsCategoryChange,
  activeSettingsCategory,
}) => {
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { colors } = useTheme();
  const { user } = useAuth();
  const [internalIsCollapsed, setInternalIsCollapsed] = useState(true);
  const [showSettingsSidebar, setShowSettingsSidebar] = useState(false);

  // Use external state if provided, otherwise use internal state
  const isCollapsed =
    externalIsCollapsed !== undefined
      ? externalIsCollapsed
      : internalIsCollapsed;
  const setIsCollapsed = useCallback(
    (collapsed: boolean) => {
      if (externalIsCollapsed !== undefined) {
        onCollapseChange?.(collapsed);
      } else {
        setInternalIsCollapsed(collapsed);
      }
    },
    [externalIsCollapsed, onCollapseChange]
  );

  const screenWidth = Dimensions.get('window').width;
  const isWebLayout = Platform.OS === 'web' && screenWidth >= 768;

  const navItems = [
    { key: 'HomeTab', label: 'Home', icon: 'home-outline', iconActive: 'home' },
    {
      key: 'ColourAnalysis',
      label: 'Analysis',
      icon: 'color-palette-outline',
      iconActive: 'color-palette',
    },
    {
      key: 'VirtualDrapeTest',
      label: 'Drape',
      icon: 'color-wand-outline',
      iconActive: 'color-wand',
    },
    {
      key: 'ColorGenerator',
      label: 'Generator',
      icon: 'color-filter-outline',
      iconActive: 'color-filter',
    },
    {
      key: 'ColorLibrary',
      label: 'Color Library',
      icon: 'library-outline',
      iconActive: 'library',
      targetTab: 'ColorLibrary',
    },
    // Moodboard hidden for now
    // { key: 'MoodBoardTab', label: 'Moodboard', icon: 'grid-outline', iconActive: 'grid' },
    // SmartWardrobe option hidden but not removed
    // { key: 'SmartWardrobe', label: 'Smart Wardrobe', icon: 'shirt-outline' },
  ];

  const settingsItems = [
    {
      key: 'account-info',
      label: 'Account Info',
      icon: 'person-outline',
      iconActive: 'person',
    },
    {
      key: 'about',
      label: 'About ColourSense',
      icon: 'information-circle-outline',
      iconActive: 'information-circle',
    },
    {
      key: 'help',
      label: 'Help & Support',
      icon: 'help-circle-outline',
      iconActive: 'help-circle',
    },
    {
      key: 'tutorial',
      label: 'Start Tutorial',
      icon: 'play-circle-outline',
      iconActive: 'play-circle',
    },
    {
      key: 'privacy-policy',
      label: 'Privacy Policy',
      icon: 'shield-checkmark-outline',
      iconActive: 'shield-checkmark',
    },
    {
      key: 'do-not-sell',
      label: 'Do Not Sell My Info',
      icon: 'hand-left-outline',
      iconActive: 'hand-left',
    },
    {
      key: 'terms',
      label: 'Terms of Service',
      icon: 'document-text-outline',
      iconActive: 'document-text',
    },
  ];

  // eslint-disable-next-line react-hooks/exhaustive-deps -- navItems is static config
  const handleTabPress = useCallback(
    (tabKey: string, clickedItemKey?: string) => {
      const navItem = navItems.find(
        (item) => item.key === (clickedItemKey ?? tabKey)
      );
      if (navItem) {
        trackButtonClick(navItem.label, 'sidebar', {
          button_type: 'sidebar_navigation',
          screen: 'sidebar',
          tab_key: tabKey,
          tab_label: navItem.label,
          is_collapsed: isCollapsed,
        });
      }

      // If we're in settings mode and clicking a main navigation item, exit settings mode
      if (showSettingsSidebar && tabKey !== 'SettingsTab') {
        setShowSettingsSidebar(false);
      }

      if (onTabPress) {
        onTabPress(tabKey);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- navItems is static config
    [onTabPress, isCollapsed, showSettingsSidebar]
  );

  const _handleBackToMainSidebar = () => {
    setShowSettingsSidebar(false);
    // Expand the sidebar when going back to main
    setIsCollapsed(false);
    if (onTabPress) {
      onTabPress('HomeTab');
    }
  };

  const handleSettingsCategoryPress = (category: string) => {
    if (onSettingsCategoryChange) {
      onSettingsCategoryChange(category);
    }
  };

  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const leaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sidebarRef = useRef<View>(null);
  const settingsRef = useRef<View>(null);

  const clearLeaveTimeout = useCallback(() => {
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }
  }, []);

  const handleSidebarHoverIn = useCallback(() => {
    clearLeaveTimeout();
    if (isCollapsed) {
      setIsCollapsed(false);
    }
  }, [isCollapsed, clearLeaveTimeout, setIsCollapsed]);

  const handleSidebarHoverOut = useCallback(() => {
    if (!isCollapsed) {
      leaveTimeoutRef.current = setTimeout(() => {
        leaveTimeoutRef.current = null;
        setIsCollapsed(true);
      }, 120);
    }
  }, [isCollapsed, setIsCollapsed]);

  useEffect(() => () => clearLeaveTimeout(), [clearLeaveTimeout]);

  // Collapse when clicking outside sidebar (web): main content or anywhere else
  useEffect(() => {
    if (!isWebLayout || isCollapsed) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const main = sidebarRef.current;
      const settings = settingsRef.current;
      const insideMain =
        main &&
        typeof (main as unknown as { contains?: (n: Node) => boolean })
          .contains === 'function' &&
        (main as unknown as { contains: (n: Node) => boolean }).contains(
          target
        );
      const insideSettings =
        settings &&
        typeof (settings as unknown as { contains?: (n: Node) => boolean })
          .contains === 'function' &&
        (settings as unknown as { contains: (n: Node) => boolean }).contains(
          target
        );
      if (!insideMain && !insideSettings) {
        setIsCollapsed(true);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isWebLayout, isCollapsed, setIsCollapsed]);

  // Sync settings sidebar with activeTab - defer setState to avoid sync setState in effect
  useEffect(() => {
    const id = setTimeout(() => {
      if (activeTab === 'SettingsTab') {
        setShowSettingsSidebar(true);
      } else {
        setShowSettingsSidebar(false);
      }
    }, 0);
    return () => clearTimeout(id);
  }, [activeTab]);

  if (!isWebLayout) {
    return null;
  }

  return (
    <>
      {/* Main Sidebar: hover to expand when collapsed, click outside to collapse */}
      <Pressable
        ref={sidebarRef}
        style={[
          styles.container,
          {
            width: isCollapsed
              ? SIDEBAR_WIDTH_COLLAPSED
              : SIDEBAR_WIDTH_EXPANDED,
            backgroundColor: colors.panelBackground ?? colors.background,
            borderRightColor: colors.border,
          },
        ]}
        onHoverIn={handleSidebarHoverIn}
        onHoverOut={handleSidebarHoverOut}
      >
        {/* Navigation Items */}
        <ScrollView
          style={styles.navSection}
          contentContainerStyle={styles.navSectionContent}
          keyboardShouldPersistTaps="handled"
        >
          {navItems.map((item) => {
            const targetTab =
              'targetTab' in item
                ? (item as { targetTab?: string }).targetTab
                : undefined;
            const tabKey = targetTab ?? item.key;
            const isActive =
              activeTab === item.key || (targetTab && activeTab === targetTab);
            const isHighlighted = isActive || hoveredItem === item.key;
            return (
              <Pressable
                key={item.key}
                style={({ pressed: _pressed }) => [
                  styles.navItem,
                  {
                    paddingHorizontal: isCollapsed ? 10 : 14,
                    paddingVertical: isCollapsed ? 10 : 10,
                    marginVertical: isCollapsed ? 1 : 2,
                    justifyContent: isCollapsed ? 'center' : 'flex-start',
                    flexDirection: isCollapsed ? 'column' : 'row',
                    alignItems: 'center',
                    backgroundColor: isHighlighted
                      ? (colors.primary ?? '#6366f1') + '12'
                      : 'transparent',
                  },
                ]}
                onPress={() => handleTabPress(tabKey, item.key)}
                onHoverIn={() => setHoveredItem(item.key)}
                onHoverOut={() => setHoveredItem(null)}
              >
                <Ionicons
                  name={
                    (isHighlighted
                      ? item.iconActive
                      : item.icon) as React.ComponentProps<
                      typeof Ionicons
                    >['name']
                  }
                  size={22}
                  color={isHighlighted ? colors.primary : colors.secondary}
                />
                <Text
                  style={[
                    isCollapsed ? styles.navLabelCollapsed : styles.navLabel,
                    {
                      color: isHighlighted ? colors.primary : colors.secondary,
                      marginLeft: isCollapsed ? 0 : 12,
                      marginTop: isCollapsed ? 4 : 0,
                    },
                  ]}
                  numberOfLines={isCollapsed ? 2 : 1}
                  ellipsizeMode="tail"
                >
                  {item.label}
                </Text>
              </Pressable>
            );
          })}
        </ScrollView>

        {/* Upgrade: show for free, plus, trial, pay-per-scan (navigate to Pricing) */}
        {user && user.subscription_plan !== 'pro' && (
          <View style={styles.upgradeButtonContainer}>
            <Pressable
              style={({ pressed }) => [
                styles.upgradeButton,
                {
                  backgroundColor: colors.primary,
                  minWidth: isCollapsed ? 52 : undefined,
                  paddingHorizontal: isCollapsed ? 10 : 14,
                  paddingVertical: isCollapsed ? 10 : 10,
                  justifyContent: isCollapsed ? 'center' : 'flex-start',
                  flexDirection: isCollapsed ? 'column' : 'row',
                  alignItems: 'center',
                },
                hoveredItem === 'upgrade' && {
                  backgroundColor: colors.primary + 'E6',
                },
                pressed && {
                  backgroundColor: colors.primary + 'CC',
                },
              ]}
              onPress={() => {
                trackButtonClick('Upgrade', 'sidebar', {
                  button_type: 'sidebar_upgrade',
                  screen: 'sidebar',
                  location: 'bottom',
                  is_collapsed: isCollapsed,
                });
                navigation.navigate('Pricing');
              }}
              onHoverIn={() => setHoveredItem('upgrade')}
              onHoverOut={() => setHoveredItem(null)}
            >
              <Ionicons
                name="diamond-outline"
                size={22}
                color={colors.onPrimary ?? colors.background}
              />
              <Text
                style={[
                  styles.upgradeButtonText,
                  isCollapsed && styles.upgradeButtonTextCollapsed,
                  {
                    color: colors.onPrimary ?? colors.background,
                    marginLeft: isCollapsed ? 0 : 12,
                    marginTop: isCollapsed ? 4 : 0,
                  },
                ]}
                numberOfLines={isCollapsed ? 2 : 1}
                ellipsizeMode="tail"
              >
                Upgrade
              </Text>
            </Pressable>
          </View>
        )}
      </Pressable>

      {/* Settings Sidebar - Only shown when in settings mode */}
      {showSettingsSidebar && (
        <View
          ref={settingsRef}
          style={[
            styles.settingsSidebar,
            {
              backgroundColor: colors.panelBackground ?? colors.background,
              borderRightColor: colors.border,
              left: isCollapsed
                ? SIDEBAR_WIDTH_COLLAPSED
                : SIDEBAR_WIDTH_EXPANDED,
            },
          ]}
        >
          {/* Settings Header */}
          <View style={[styles.header, { borderBottomColor: colors.border }]}>
            <View style={styles.headerContent}>
              <View style={styles.logoContainer}>
                <Text style={[styles.settingsTitle, { color: colors.text }]}>
                  Settings
                </Text>
              </View>
            </View>
          </View>

          {/* Settings Navigation Items */}
          <ScrollView
            style={styles.navSection}
            contentContainerStyle={styles.navSectionContent}
            keyboardShouldPersistTaps="handled"
          >
            {settingsItems.map((item) => (
              <Pressable
                key={item.key}
                style={({ pressed: _pressed }) => [
                  styles.navItem,
                  {
                    paddingHorizontal: 16,
                    paddingVertical: 12,
                    marginVertical: 3,
                    justifyContent: 'space-between',
                    backgroundColor: 'transparent',
                  },
                ]}
                onPress={() => handleSettingsCategoryPress(item.key)}
                onHoverIn={() => setHoveredItem(item.key)}
                onHoverOut={() => setHoveredItem(null)}
              >
                <View style={styles.settingsItemContent}>
                  <Ionicons
                    name={
                      (activeSettingsCategory === item.key ||
                      hoveredItem === item.key
                        ? item.iconActive
                        : item.icon) as React.ComponentProps<
                        typeof Ionicons
                      >['name']
                    }
                    size={24}
                    color={
                      activeSettingsCategory === item.key ||
                      hoveredItem === item.key
                        ? colors.primary
                        : colors.secondary
                    }
                  />
                  <Text
                    style={[
                      styles.navLabel,
                      {
                        color:
                          activeSettingsCategory === item.key ||
                          hoveredItem === item.key
                            ? colors.primary
                            : colors.secondary,
                      },
                    ]}
                  >
                    {item.label}
                  </Text>
                </View>
                <Ionicons
                  name="chevron-forward"
                  size={20}
                  color={
                    activeSettingsCategory === item.key
                      ? colors.primary
                      : colors.secondary
                  }
                />
              </Pressable>
            ))}
          </ScrollView>
        </View>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    height: '100%',
    borderRightWidth: StyleSheet.hairlineWidth,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0,
    shadowRadius: 0,
    elevation: 0,
    zIndex: 1000,
    flexDirection: 'column',
    ...(Platform.OS === 'web' && {
      transition: 'width 0.2s ease-out',
    }),
  },
  header: {
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },

  backButton: {
    position: 'absolute',
    right: 16,
    top: 24,
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 4,
    zIndex: 1002,
  },
  logoContainer: {
    flex: 1,
  },
  logo: {
    width: 200,
    height: 48,
  },
  iconContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  collapsedIcon: {
    width: 48,
    height: 48,
  },
  navSection: {
    flex: 1,
    paddingVertical: 16,
    paddingHorizontal: 10,
  },
  navSectionContent: {
    flexGrow: 1,
  },
  upgradeButtonContainer: {
    paddingHorizontal: 10,
    paddingVertical: 12,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: 'rgba(0,0,0,0.06)',
  },
  navItem: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    minHeight: 40,
  },
  navLabel: {
    marginLeft: 12,
    fontSize: 14,
    fontWeight: '500',
  },
  navLabelCollapsed: {
    fontSize: 10,
    fontWeight: '500',
    textAlign: 'center',
    lineHeight: 12,
    maxWidth: 52,
  },
  bottomSection: {
    paddingHorizontal: 12,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.1)',
    marginTop: 'auto',
  },
  actionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    marginVertical: 4,
    borderRadius: 14,
  },
  actionLabel: {
    marginLeft: 14,
    fontSize: 14,
    fontWeight: '600',
  },
  upgradeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginVertical: 0,
    borderRadius: 12,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0,
    shadowRadius: 0,
    elevation: 0,
  },
  upgradeButtonText: {
    marginLeft: 12,
    fontSize: 14,
    fontWeight: '600',
  },
  upgradeButtonTextCollapsed: {
    fontSize: 10,
    fontWeight: '600',
    textAlign: 'center',
    lineHeight: 12,
    maxWidth: 52,
  },
  settingsSidebar: {
    height: '100%',
    width: 320,
    borderRightWidth: StyleSheet.hairlineWidth,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0,
    shadowRadius: 0,
    elevation: 0,
    zIndex: 1001, // Higher than main sidebar
    position: 'absolute' as const,
    left: 0, // Will be dynamically positioned after main sidebar
    top: 0,
  },
  settingsTitle: {
    fontSize: 20,
    fontWeight: '700',
  },
  settingsItemContent: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
});

export default Sidebar;
